import React, {useEffect, useRef, useState} from "react";
import {Form} from "react-bootstrap";
import {useForm} from "react-hook-form";
import {makeGetRequest, makePatchRequest} from "Services/ServicesUtils";
import {API_AUDITS} from "Models/Audit/AuditEndpoints";
import {insertNotification} from "Utils/NotificationsUtils";
import {GSPSTextArea} from "Components/Common/GSPSTextAreaInput";
import {useNavigate} from "react-router-dom";
import PropTypes from "prop-types";
import {Button} from "@mui/material";
import {InfoForm} from "./InfoForm.styles";
import {useUpdateAuditMutation} from "api/AuditTableAPI";
import {SVPToolTipIcon} from "Components/ToolTip/SVPToolTipIcon";
import {ContactsAutoComplete} from "Components/Contacts/ContactsAutoComplete";
import {SVPSubmitButton} from "Components/Buttons/SVPSubmitButton";
import {AuditStatusID} from "Models/Audit/AuditConstants";

export const AuditorInformation = ({
    audit,
    setAudit,
    user,
    users,
    isReadOnly,
    isPublicAudit = false,
}) => {
    const {handleSubmit, register, setValue, formState: {errors}} = useForm();
    const [selectedAuditor, setSelectedAuditor] = useState();
    const [auditStatuses, setAuditStatuses] = useState([]);
    const [auditStatus, setAuditStatus] = useState();
    const [selectedReviewer, setSelectedReviewer] = useState(audit?.owner);
    const [isSubmittingForReview, setIsSubmittingForReview] = useState(false);

    const navigate = useNavigate();
    const autosaveNotesTimeout = useRef(null);
    const [notes, setNotes] = useState(audit?.auditor_additional_notes ?? "");
    const [updateAudit] = useUpdateAuditMutation();

    useEffect(() => {
        if (!isReadOnly) {
            makeGetRequest(`${API_AUDITS.auditStatuses}`).then(
                (res) => {
                    setAuditStatuses(res.results);
                },
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        setNotes(audit?.auditor_additional_notes ?? "");
        setSelectedAuditor(audit?.auditor ?? user);
        setSelectedReviewer(audit?.reviewer ?? audit?.owner);
        setAuditStatus(auditStatuses?.find((status) => status.id === audit?.audit_status?.id)?.name
            ?.trim().toLowerCase());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [audit]);

    const onSubmit = async () => {
        try {
            setIsSubmittingForReview(true);
            const updatedAudit = {
                ...audit,
                auditor_id: selectedAuditor?.id,
                reviewer_id: selectedReviewer?.id,
                auditor_additional_notes: notes,
                audit_status_id: AuditStatusID.IN_REVIEW,
                audit_type_id: audit.audit_type.id,
                description_type_id: audit.description_type?.id,
                location_id: audit.location.id,
                project_id: audit.project_id,
                lines: audit.audit_lines,
                areas: audit.audit_areas,
            };
            const jsonRes = await updateAudit({id: audit.id, updatedItem: updatedAudit}).unwrap();
            insertNotification("Success", `Audit ${jsonRes.id} has been updated.`, "success");
            setAudit(jsonRes);
        } catch (e) {
            insertNotification("Error", `Can't update Inspection: ${e.message || e}`, "error");
        } finally {
            setIsSubmittingForReview(false);
        }
    };

    const updateNotes = async (notes) => {
        setAudit((currentAudit) => ({
            ...currentAudit,
            auditor_additional_notes: notes,
        }));

        clearTimeout(autosaveNotesTimeout.current);
        autosaveNotesTimeout.current = setTimeout(async () => {
            if (audit && notes !== audit?.auditor_additional_notes && !isReadOnly) {
                const updatedAudit = {
                    auditor_additional_notes: notes,
                };
                makePatchRequest(`${API_AUDITS.audits}${audit.id}/`, updatedAudit);
            }
        }, 1000);
    };

    const handleBackToDashboard = () => {
        navigate("/dashboard");
    };

    return (
        <InfoForm>
            <div className="container" id="audit-info">
                <Form onSubmit={handleSubmit(onSubmit)}>
                    <h1 className="subtitle-md">
                        Inspection Information
                    </h1>
                    <div className="row">
                        <div className="col-md-8 col-12 ps-4 d-flex flex-column justify-content-between">
                            <div className="row">
                                <h2 className="col-md-5 col-12 body-md d-flex align-items-center">
                                    Inspector
                                </h2>
                                <div className="col mt-4 mb-4">
                                    <ContactsAutoComplete
                                        users={users}
                                        contact={selectedAuditor ?? audit?.auditor}
                                        isDisabled={true}
                                    />
                                </div>
                            </div>
                            <div className="row">
                                {!isPublicAudit && auditStatus !== "in review" && users &&
                                    <>
                                        <h2 className="col-md-5 col-12 body-md d-flex align-items-center">
                                            Reviewer
                                        </h2>
                                        <div className="col">
                                            <ContactsAutoComplete
                                                users={users}
                                                setContact={setSelectedReviewer}
                                                contact={selectedReviewer}
                                            />
                                        </div>
                                    </>
                                }
                            </div>
                        </div>
                        {!isPublicAudit &&
                            <div className="col-md-4 col-12">
                                <h2 className="body-md mb-2">
                                    Additional Notes
                                    <SVPToolTipIcon
                                        fontSize="20px"
                                        placement="top"
                                        message=" This description box is visible only to people inside your
                                            organization."
                                        rightMargin = "5px"
                                    />
                                </h2>
                                <GSPSTextArea
                                    value={notes}
                                    labelName={"Additional Notes"}
                                    id={"additionalNotes"}
                                    controlType={"textarea"}
                                    isRequired={false}
                                    register={register}
                                    isDisabled={isReadOnly || auditStatus === "in review"}
                                    rows={7}
                                    errors={errors}
                                    setValue={setValue}
                                    charLimit={700}
                                    setDescription={setNotes}
                                    autoSave={true}
                                    updateOnChange={updateNotes}
                                    hiddenCharLimit={true}
                                    hiddenLabel={true}
                                />
                            </div>
                        }
                    </div>
                    {!isReadOnly &&
                        [AuditStatusID.IN_PROGRESS, AuditStatusID.EDITS_REQUIRED].includes(audit?.audit_status.id) && (
                        <>
                            <div className="d-flex justify-content-end gap-2">
                                <Button
                                    onClick={handleBackToDashboard}
                                    variant="contained"
                                    className="float-end me-3 mt-3 cta-btn-secondary"
                                >
                                    Navigate To Dashboard
                                </Button>
                                <SVPSubmitButton
                                    isLoading={!isSubmittingForReview}
                                    className="mt-3 cta-btn"
                                    buttonText="Submit For Review"
                                    buttonLoadingText="Submitting..."
                                    disabled={auditStatus === "in review"}
                                />
                            </div>
                        </>
                    )}
                </Form>
            </div>
        </InfoForm>
    );
};

AuditorInformation.propTypes = {
    audit: PropTypes.object,
    setAudit: PropTypes.func,
    user: PropTypes.object,
    users: PropTypes.array,
    isReadOnly: PropTypes.bool,
    isPublicAudit: PropTypes.bool,
};
